import React from 'react';
import ProfileFormSheet from '../modals/ProfileFormSheet';

const EVIDENCE_MODES = [
  {
    mode: 'single latest',
    key: 'single_latest',
    description: 'Default. When the student submits, the newest submission is included automatically.',
  },
  {
    mode: 'single best',
    key: 'single_best',
    description: 'Highest-scoring submission for that form is kept in the portfolio.',
  },
  {
    mode: 'each submission in LP',
    key: 'each_submission_in_lp',
    description: 'Every attempt in the learning period is added to evidence.',
  },
  {
    mode: 'single manual',
    key: 'single_manual',
    description: 'Nothing is auto-included. You choose which submission is evidence.',
  },
];

export function PortfolioEvidenceHelpContent() {
  return (
    <div className="ws-portfolio-help">
      <p className="ws-portfolio-help-lead">
        A portfolio has two layers: <strong>what forms count for the learning period</strong> and{' '}
        <strong>which student work is actually in the package</strong>.
      </p>

      <div className="ws-portfolio-help-card">
        <h3 className="ws-portfolio-help-heading">Assigned vs included</h3>
        <dl className="ws-portfolio-help-dl">
          <div>
            <dt>Form assigned</dt>
            <dd>
              The form is designated for this learning period. The student can fill it out. Removing
              a form from the portfolio unassigns it from the LP package.
            </dd>
          </div>
          <div>
            <dt>Submission in portfolio</dt>
            <dd>
              A specific student submission is included as evidence. You can remove a submission from
              the portfolio while keeping the form assigned — for example after a VDM flag or while
              waiting for a redo.
            </dd>
          </div>
        </dl>
      </div>

      <div className="ws-portfolio-help-card">
        <h3 className="ws-portfolio-help-heading">Why both?</h3>
        <p>
          One assignment can produce <strong>multiple submissions</strong> (retakes, corrections,
          drafts). You usually do not want every attempt in the final package — only the right
          one(s). That is why assignment and evidence are stored separately.
        </p>
      </div>

      <div className="ws-portfolio-help-card">
        <h3 className="ws-portfolio-help-heading">Evidence modes</h3>
        <p className="ws-portfolio-help-muted">
          Set per form when assigning (or on the portfolio). Controls what happens when the student
          submits.
        </p>
        <ul className="ws-portfolio-help-modes">
          {EVIDENCE_MODES.map((item) => (
            <li key={item.key}>
              <strong>{item.mode}</strong>
              <span>{item.description}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="ws-portfolio-help-card ws-portfolio-help-card--note">
        <h3 className="ws-portfolio-help-heading">Status labels in this view</h3>
        <ul className="ws-portfolio-help-status-list">
          <li>
            <strong>Assigned — awaiting submission</strong>
            <span>Form is in the portfolio; student has not submitted yet.</span>
          </li>
          <li>
            <strong>Submitted — not yet included in portfolio</strong>
            <span>
              Student submitted, but evidence mode is manual or sync has not run yet. Use include
              actions when available, or have the student re-submit for auto modes.
            </span>
          </li>
          <li>
            <strong>In portfolio</strong>
            <span>That submission is in the LP evidence package.</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default function PortfolioEvidenceHelpSheet({ open, onClose }) {
  return (
    <ProfileFormSheet
      open={open}
      onClose={onClose}
      title="Portfolio forms & evidence"
      titleId="ws-portfolio-help-title"
      subtext="How assignments, submissions, and evidence modes work together."
      overlayClassName="ws-portfolio-help-overlay"
      panelClassName="ws-portfolio-help-panel"
      footer={
        <button
          type="button"
          className="profile-sheet-btn profile-sheet-btn--secondary"
          onClick={onClose}
        >
          Close
        </button>
      }
    >
      <PortfolioEvidenceHelpContent />
    </ProfileFormSheet>
  );
}
